/**
 * ExplorationLayout - Layout algorithm for positioning explored nodes
 * relative to the source node using dagre for hierarchical graph layout.
 *
 * Uses a single unified dagre graph to compute proper depth-aware positioning
 * so that deep exploration chains (A → B → C → D) are laid out across multiple
 * columns/ranks rather than being flattened into a single column.
 *
 * Layout strategy:
 * - ALL nodes (incoming and outgoing) are placed in ONE dagre graph.
 * - Outgoing edges: dagre edge goes sourceNode → exploredNode (children placed to the right).
 * - Incoming edges: dagre edge goes exploredNode → sourceNode (parents placed to the left).
 * - This means dagre naturally places parents at lower ranks (left) and
 *   children at higher ranks (right) relative to the focused node.
 * - Edge crossover minimisation works globally since everything is in one graph.
 * - Each relationship group's edges are color-coded using the ADS chart palette.
 * - Loading (placeholder) nodes are skipped until their queries resolve.
 */

import type { Node, Edge } from '@xyflow/react';
import { graphlib, layout } from 'dagre';

import type { ObjectEdgeData } from '../nodes/ObjectEdge';
import type { ObjectNodeData } from '../nodes/ObjectNode';
import type { ExplorationDirection } from '../services/cypherQueryService';
import { getRelationshipColor } from '../services/cypherQueryService';

// Layout constants
const NODE_SEP = 40; // Vertical separation between nodes in the same rank
const RANK_SEP = 400; // Horizontal separation between ranks (depth levels)
const NODE_WIDTH = 220; // Approximate node width for dagre
const NODE_HEIGHT = 60; // Approximate node height for dagre

export interface ExplorationLayoutResult {
	nodes: Node<ObjectNodeData>[];
	edges: Edge<ObjectEdgeData>[];
}

/**
 * A relationship group for multi-relationship layout.
 */
export interface RelationshipGroup {
	/** The relationship type label */
	relationshipType: string;
	/** Optional short display label for edges (e.g. extracted verb) */
	displayLabel?: string;
	/** The direction of this relationship */
	direction: ExplorationDirection;
	/** The source node for this group */
	sourceNode: Node<ObjectNodeData>;
	/** The explored nodes for this relationship (empty if loading) */
	exploredNodes: Node<ObjectNodeData>[];
	/** Whether this group is currently loading */
	isLoading?: boolean;
	/** Color index for this relationship (assigned by caller based on selection order) */
	colorIndex: number;
}

/**
 * Lays out multiple relationship groups using a single unified dagre graph.
 *
 * All nodes — regardless of direction — are placed in one dagre LR graph.
 * Edge direction in dagre is determined by the relationship direction:
 *   - Outgoing: source → explored (explored placed to the right)
 *   - Incoming: explored → source (explored placed to the left)
 *
 * This ensures that:
 *   - Parents of the focused node appear to the LEFT.
 *   - Children of the focused node appear to the RIGHT.
 *   - Children of parents (siblings) appear at the correct depth level.
 *   - Edge crossovers are globally minimised by dagre.
 *
 * A simple (non-multigraph) dagre graph is used. When multiple relationship
 * types create edges between the same node pair in the same direction, only
 * one dagre edge is added (dagre overwrites silently). All visual edges are
 * still emitted for @xyflow/react rendering.
 *
 * Edges are color-coded based on the group's colorIndex.
 */
export function layoutMultipleRelationshipGroups(
	groups: RelationshipGroup[],
	rootNodeId?: string,
): ExplorationLayoutResult {
	if (groups.length === 0) {
		return { nodes: [], edges: [] };
	}

	// Filter to only resolved (non-loading) groups with nodes
	const resolvedGroups = groups.filter(
		(group) => !group.isLoading && group.exploredNodes.length > 0,
	);

	if (resolvedGroups.length === 0) {
		return { nodes: [], edges: [] };
	}

	// --- Build a single unified dagre graph (simple, not multigraph) ---

	const dagreGraph = new graphlib.Graph();
	dagreGraph.setDefaultEdgeLabel(() => ({}));
	dagreGraph.setGraph({
		rankdir: 'LR',
		nodesep: NODE_SEP,
		ranksep: RANK_SEP,
		marginx: 0,
		marginy: 0,
	});

	// Identify the root source node — the focused node that anchors the layout.
	//
	// When rootNodeId is provided (from the focused node), use it directly.
	// This is critical for circular references (A→B→A) where the heuristic
	// of "source that never appears as explored" fails because both nodes
	// appear as explored in each other's groups.
	//
	// Fallback: find the first source that is never an explored node,
	// or use the first group's source.
	let rootSourceId: string | undefined;
	let rootSourceNode: Node<ObjectNodeData> | undefined;

	if (rootNodeId) {
		// Explicit root — find the matching source node from any group
		for (const group of resolvedGroups) {
			if (group.sourceNode.id === rootNodeId) {
				rootSourceId = rootNodeId;
				rootSourceNode = group.sourceNode;
				break;
			}
		}
	}

	if (!rootSourceId || !rootSourceNode) {
		// Heuristic fallback: pick the first source that never appears as explored
		const allExploredIds = new Set<string>();
		for (const group of resolvedGroups) {
			for (const node of group.exploredNodes) {
				allExploredIds.add(node.id);
			}
		}

		for (const group of resolvedGroups) {
			if (!allExploredIds.has(group.sourceNode.id)) {
				rootSourceId = group.sourceNode.id;
				rootSourceNode = group.sourceNode;
				break;
			}
		}
		// Final fallback: use the first group's source
		if (!rootSourceId || !rootSourceNode) {
			rootSourceId = resolvedGroups[0].sourceNode.id;
			rootSourceNode = resolvedGroups[0].sourceNode;
		}
	}

	// Map from node ID → original explored node data (for output)
	const exploredNodeMap = new Map<string, Node<ObjectNodeData>>();

	// Track IDs of nodes that need to be emitted (explored nodes only)
	const exploredNodeIds = new Set<string>();

	// Collect visual edge entries for @xyflow/react output
	const edgeEntries: Array<{
		id: string;
		source: string;
		target: string;
		relationshipType: string;
		displayLabel?: string;
		color: string;
	}> = [];

	// Add all nodes and edges to the single dagre graph
	for (const group of resolvedGroups) {
		const color = getRelationshipColor(group.colorIndex);

		// Add source node to dagre (if not already present)
		if (!dagreGraph.hasNode(group.sourceNode.id)) {
			dagreGraph.setNode(group.sourceNode.id, {
				width: NODE_WIDTH,
				height: NODE_HEIGHT,
			});
		}

		// Add each explored node and the dagre edge
		for (let nodeIndex = 0; nodeIndex < group.exploredNodes.length; nodeIndex++) {
			const node = group.exploredNodes[nodeIndex];

			if (!dagreGraph.hasNode(node.id)) {
				dagreGraph.setNode(node.id, {
					width: NODE_WIDTH,
					height: NODE_HEIGHT,
				});
			}

			exploredNodeIds.add(node.id);
			exploredNodeMap.set(node.id, node);

			// Dagre edge direction depends on the relationship direction:
			// - Outgoing: source → explored (explored is placed at a higher rank, to the right)
			// - Incoming: explored → source (explored is placed at a lower rank, to the left)
			//
			// In a simple graph, duplicate setEdge calls for the same pair
			// are silently ignored by dagre — this is fine since we only need
			// one structural edge per pair for layout. All visual edges are
			// still tracked in edgeEntries below.
			if (group.direction === 'outgoing') {
				dagreGraph.setEdge(group.sourceNode.id, node.id);
			} else {
				// Incoming: the explored node (parent) is "before" the source in the graph
				dagreGraph.setEdge(node.id, group.sourceNode.id);
			}

			// Visual edge for @xyflow/react (always source→target in the
			// relationship's natural direction, regardless of dagre edge direction)
			const isOutgoing = group.direction === 'outgoing';
			edgeEntries.push({
				id: `focus-${group.sourceNode.id}-${group.relationshipType}-${node.id}-${nodeIndex}`,
				source: isOutgoing ? group.sourceNode.id : node.id,
				target: isOutgoing ? node.id : group.sourceNode.id,
				relationshipType: group.relationshipType,
				displayLabel: group.displayLabel,
				color,
			});
		}
	}

	// --- Run dagre layout ---
	layout(dagreGraph);

	// --- Compute offset so the root source node stays at its actual screen position ---
	// Dagre returns centre-based coordinates; @xyflow/react uses top-left.
	const dagreRootPos = dagreGraph.node(rootSourceId);
	const rootCenterX = rootSourceNode.position.x + NODE_WIDTH / 2;
	const rootCenterY = rootSourceNode.position.y + NODE_HEIGHT / 2;

	const offsetX = rootCenterX - dagreRootPos.x;
	const offsetY = rootCenterY - dagreRootPos.y;

	// --- Emit positioned explored nodes ---
	const resultNodes: Node<ObjectNodeData>[] = [];

	for (const nodeId of exploredNodeIds) {
		// Never emit the root source — it's already in the base graph
		if (nodeId === rootSourceId) {
			continue;
		}

		const dagreNode = dagreGraph.node(nodeId);
		const originalNode = exploredNodeMap.get(nodeId);
		if (!originalNode || !dagreNode) {
			continue;
		}

		// Convert dagre centre coords to top-left coords for @xyflow/react
		const finalX = dagreNode.x + offsetX - NODE_WIDTH / 2;
		const finalY = dagreNode.y + offsetY - NODE_HEIGHT / 2;

		resultNodes.push({
			...originalNode,
			position: {
				x: finalX,
				y: finalY,
			},
		});
	}

	// --- Emit edges ---
	const resultEdges: Edge<ObjectEdgeData>[] = edgeEntries.map((entry) => ({
		id: entry.id,
		type: 'objectEdge',
		source: entry.source,
		target: entry.target,
		data: {
			relationshipType: entry.relationshipType,
			displayLabel: entry.displayLabel,
			color: entry.color,
		},
	}));

	return { nodes: resultNodes, edges: resultEdges };
}
